import {
  type CreateDraftMessageInputDto,
  type CreateInboxDraftMutation,
  type FetchResult,
  type MutationResult,
  useCreateInboxDraftMutation,
} from "@qw/graphql";
import { useToast } from "@qw/ui";

import { useCallback } from "react";
import { useDraftsCompose } from "./use-drafts-compose";

export type UseCreateDraftReturn = [
  (input?: CreateDraftMessageInputDto) => Promise<FetchResult<CreateInboxDraftMutation> | undefined>,
  MutationResult<CreateInboxDraftMutation>,
];

export const useCreateDraft = (): UseCreateDraftReturn => {
  const { toast } = useToast();
  const { openDraft } = useDraftsCompose();
  const [createDraftMutation, result] = useCreateInboxDraftMutation();

  const createDraft = useCallback(
    (input?: CreateDraftMessageInputDto) =>
      createDraftMutation({
        variables: { input: input ?? {} },
        onCompleted: (data) => {
          if (!data?.createInboxDraft) {
            throw new Error("Failed to create draft");
          }
          openDraft(data.createInboxDraft.id);
        },
        onError: (error) => {
          toast({
            title: "Error creating draft",
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
        refetchQueries: ["InboxDrafts", "InboxDraftsCount"],
      }),
    [createDraftMutation, toast, openDraft]
  );

  return [createDraft, result];
};
